import React, { useEffect, useState } from 'react';
import { Play, ArrowRight } from 'lucide-react';
import { SiteContent } from './admin/types';
import { getYouTubeVideoId, getYouTubeThumbnail, getYouTubeEmbedUrl } from '../utils/youtube';

interface HeroProps {
  scrollToSection: (sectionId: string) => void;
}

const Hero: React.FC<HeroProps> = ({ scrollToSection }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [content, setContent] = useState<SiteContent['hero']>({
    title: 'Your Trusted Team of',
    subtitle: 'Independent Licensed Agents',
    ctaText1: 'Meet Our Team',
    ctaText2: 'Learn More',
    videoTitle: 'Watch Our Team Introduction',
    videoUrl: 'https://www.youtube.com/watch?v=cPQZIrUbzVA&feature=youtu.be'
  });

  useEffect(() => {
    const loadContent = async () => {
      try {
        const response = await fetch('/data/content.json');
        if (response.ok) {
          const data = await response.json();
          if (data.hero) {
            setContent(data.hero);
          }
        }
      } catch (error) {
        console.error('Error loading hero content:', error);
      }
    };
    loadContent();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const [showVideo, setShowVideo] = useState(false);
  const [videoId, setVideoId] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');

  useEffect(() => {
    if (content.videoUrl) {
      const id = getYouTubeVideoId(content.videoUrl);
      setVideoId(id);
      if (id) {
        setThumbnailUrl(getYouTubeThumbnail(id));
      }
    }
  }, [content.videoUrl]);

  const handleVideoClick = () => {
    if (videoId) {
      setShowVideo(true);
    }
  };

  return (
    <header 
      id="hero" 
      className="relative bg-gradient-to-br from-bbha-plum-800 to-bbha-plum-900 pt-[1.125rem] lg:pt-18 min-h-screen flex items-center overflow-hidden"
      role="banner"
      aria-labelledby="hero-heading"
    >
      {/* Subtle background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-bbha-olive-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-bbha-olive-300 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-pulse" style={{animationDelay: '3s'}}></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center py-6 lg:py-12">
          {/* Left Column - Content */}
          <div className={`space-y-4 lg:space-y-8 order-2 lg:order-1 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="space-y-2 lg:space-y-4">
              <h1 
                id="hero-heading"
                className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight drop-shadow-lg"
              >
                {content.title}
                <span className="text-bbha-cream-100 block">{content.subtitle}</span>
              </h1>
            </div>

            {/* CTA Buttons */}
            <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
              <button
                onClick={() => scrollToSection('agents')}
                className="bg-bbha-cream-50 text-bbha-plum-800 px-8 lg:px-10 py-4 lg:py-5 rounded-lg font-semibold hover:bg-bbha-cream-100 transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 shadow-xl hover:shadow-2xl text-xl lg:text-2xl min-h-[56px] lg:min-h-[64px] w-full sm:w-auto"
                aria-label="View our team of Medicare agents"
              >
                {content.ctaText1}
                <ArrowRight size={20} aria-hidden="true" />
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="border-2 border-bbha-cream-50 text-bbha-cream-50 px-8 lg:px-10 py-4 lg:py-5 rounded-full font-semibold hover:bg-bbha-cream-50 hover:text-bbha-plum-900 transition-all duration-300 hover:scale-105 text-xl lg:text-2xl backdrop-blur-sm min-h-[56px] lg:min-h-[64px] w-full sm:w-auto shadow-lg"
                aria-label="Learn more about our Medicare services"
              >
                {content.ctaText2}
              </button>
            </div>
          </div>

          {/* Right Column - Video Player */}
          <div className={`relative order-1 lg:order-2 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative bg-gradient-to-br from-bbha-plum-800 to-bbha-plum-900 rounded-xl overflow-hidden shadow-2xl aspect-video video-container border-2 border-white/20 transform hover:scale-105 transition-transform duration-500">
              {showVideo && videoId ? (
                /* Embedded YouTube Video */
                <iframe
                  src={getYouTubeEmbedUrl(videoId)}
                  title={content.videoTitle}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  // YouTube requires the embedding origin to avoid player Error 153.
                  referrerPolicy="strict-origin-when-cross-origin"
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                />
              ) : (
                /* Video Thumbnail */
                <>
                  <div className="absolute inset-0">
                    {thumbnailUrl ? (
                      <img 
                        src={thumbnailUrl}
                        alt={content.videoTitle}
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="bg-gradient-to-br from-bbha-plum-500 to-bbha-plum-700 w-full h-full flex items-center justify-center">
                        <div className="text-center space-y-2">
                          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 lg:p-4 inline-flex animate-pulse">
                            <Play className="text-white" size={36} />
                          </div>
                          <p className="text-white text-xl lg:text-2xl font-medium px-3">{content.videoTitle}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Play Button Overlay */}
                  <button 
                    onClick={handleVideoClick}
                    className="absolute inset-0 flex items-center justify-center group focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent"
                    aria-label={`Play video: ${content.videoTitle}`}
                    type="button"
                  >
                    <div className="bg-black/60 backdrop-blur-sm rounded-full p-4 lg:p-6 group-hover:bg-black/80 group-hover:scale-110 group-focus:bg-black/80 group-focus:scale-110 transition-all duration-300 shadow-xl hover:shadow-2xl">
                      <Play className="text-white fill-white" size={32} aria-hidden="true" />
                    </div>
                  </button>

                  {/* Video Title Overlay */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-black/50 backdrop-blur-sm rounded-lg p-3">
                      <p className="text-white text-lg font-medium text-center">{content.videoTitle}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Hero;